import CarInfo from './Steps/CarInfo';
import CustomerInfo from './Steps/CustomerInfo';
import React, { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LocationSelection from './Steps/LocationSelection';
import { ServiceBookingProvider } from './context/ServiceBookingState';
import ServiceSlotSelection from './Steps/ServiceSlotSelection';
import Review from './Steps/Review';
import Success from './Steps/Success';
import { ServiceBookingForm, ServicePackage, Step, StepProps } from '../types';
import { useTranslation } from 'react-i18next';

/**
 * Global type declaration for the service booking modal interface.
 * This object is initialized in ServiceBooking.js and used for communication
 * between vanilla JS and React.
 */
declare global {
    interface Window {
        serviceBookingModal: {
            /** Function to open the modal with a service package */
            open?: (servicePackage: ServicePackage) => void;
            /** Flag indicating React component is initialized and ready */
            reactIsInitialized?: boolean;
            /** Pending service package data if open() was called before React init */
            pendingOpen?: ServicePackage;
        };
    }
}

/**
 * Custom event type for opening the modal with service package data
 * Example:
 * window.ServiceBooking.open({code: 'WAAP001', titel: 'Wartung lt. Herstellervorgaben Premium'});
 */
export interface ServiceBookingOpenEvent extends CustomEvent {
    detail: ServicePackage;
}

// Initial empty form state factory
const createInitialForm = (servicePackageCode: string): ServiceBookingForm => ({
    fahrzeug: {
        fabrikat_code: '',
        modell: '',
        kennzeichen: '',
        fgstnr: '',
        km: null,
        erstzulassung: '',
    },
    customer: {
        anrede: null,
        leading_title: '',
        first_name: '',
        last_name: '',
        email: '',
        telefon_privat: '',
        telefon_mobil: '',
        street: '',
        post_code: '',
        city: '',
    },
    servicetermin: null,
    service_package_code: servicePackageCode,
    standort_betriebsnummer: null,
});

function ServiceBookingApp() {
    const { t } = useTranslation();
    const [selectedServicePackage, setSelectedServicePackage] = useState<ServicePackage | null>(null);
    const [form, setForm] = useState<ServiceBookingForm>(createInitialForm(''));
    const [open, setOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Listen for external open/close events
    const handleOpen = useCallback((event: Event) => {
        const customEvent = event as ServiceBookingOpenEvent;
        const servicePackage = customEvent.detail;

        // Reset state for new booking
        setSelectedServicePackage(servicePackage);
        setForm(createInitialForm(servicePackage.code));
        setCurrentStep(0);
        setOpen(true);

        document.body.classList.add('service-booking-modal-open');
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        document.body.classList.remove('service-booking-modal-open');
    }, []);

    useEffect(() => {
        window.serviceBookingModal = window.serviceBookingModal || {};
        window.serviceBookingModal.reactIsInitialized = true;
        if (window.serviceBookingModal.pendingOpen) {
            handleOpen(new CustomEvent('service-booking:open', { detail: window.serviceBookingModal.pendingOpen }));
            delete window.serviceBookingModal.pendingOpen;
        }

        window.addEventListener('service-booking:open', handleOpen);
        window.addEventListener('service-booking:close', handleClose);

        return () => {
            window.removeEventListener('service-booking:open', handleOpen);
            window.removeEventListener('service-booking:close', handleClose);
        };
    }, [handleOpen, handleClose]);

    // Close modal when Escape key is pressed
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, handleClose]);

    // Scrolls the modal container to the top
    const scrollToTop = () => {
        containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const steps: Step[] = useMemo(() => [
        {
            label: t('step.vehicle'),
            component: CarInfo,
        },
        {
            label: t('step.personal'),
            component: CustomerInfo,
        },
        {
            label: t('step.location'),
            component: LocationSelection,
        },
        {
            label: t('step.appointment'),
            component: ServiceSlotSelection,
        },
        {
            label: t('step.review'),
            component: Review,
        },
        {
            label: t('step.success'),
            component: Success,
        },
    ], [t]);

    const StepComponent: ComponentType<StepProps> = steps[currentStep]?.component ?? (() => null);

    function close() {
        handleClose();
    }

    // Don't render if modal is closed or no service package selected
    if (!open || !selectedServicePackage) {
        return null;
    }

    function handleOnComplete() {
        setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
        scrollToTop();
    }

    function navigateToStep(step: number) {
        setCurrentStep(step);
        scrollToTop();
    }

    return (
        <ServiceBookingProvider>
            <div className="fixed inset-0 z-50 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in dark:bg-gray-900/50 print:hidden"></div>
            <div className="fixed inset-4 z-50 flex items-center justify-center print:static print:inset-auto print:block print:z-auto">
                <div ref={containerRef} className="relative flex h-full max-h-full w-full max-w-5xl flex-col overflow-y-auto bg-white px-6 pb-10 shadow-2xl print:h-auto print:max-h-none print:max-w-none print:overflow-visible print:p-0 print:shadow-none">
                    <div className="relative w-full text-left">
                        <TopBar close={close} selectedServicePackage={selectedServicePackage} />

                        {currentStep < steps.length - 1 && (
                            <Wizard steps={steps} currentStep={currentStep} setCurrentStep={setCurrentStep} />
                        )}

                        <StepComponent
                            form={form}
                            setForm={setForm}
                            onStepCompleted={handleOnComplete}
                            setIsLoading={setIsLoading}
                            isLoading={isLoading}
                            navigateToStep={navigateToStep}
                            close={close}
                        />
                    </div>

                    {isLoading && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80">
                            <div
                                className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"
                                role="status"
                                aria-label="Loading"
                            ></div>
                        </div>
                    )}
                </div>
            </div>
        </ServiceBookingProvider>
    );
}

function TopBar({ close, selectedServicePackage }: { close: () => void; selectedServicePackage: ServicePackage }) {
    const { t } = useTranslation();
    return (
        <div className={'rich-text border-b pb-5 pt-6 print:border-black print:pt-0'}>
            <div className={'flex flex-row items-center justify-between'}>
                <h3 className={'!m-0 print:text-xl'}>{selectedServicePackage.titel ?? t('modal.bookService')}</h3>

                <button className={'w-4 print:hidden'} onClick={close}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                        <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

function Wizard({
    steps,
    currentStep,
    setCurrentStep,
}: {
    steps: Step[];
    currentStep: number;
    setCurrentStep: (step: number) => void;
}) {
    // Delay animation start until after scroll completes (~400ms)
    const [showAnimation, setShowAnimation] = useState(false);

    useEffect(() => {
        // Reset animation state when step changes
        setShowAnimation(false);

        // Start animation after scroll finishes (roughly 400ms for smooth scroll)
        const timer = setTimeout(() => {
            setShowAnimation(true);
        }, 400);

        return () => clearTimeout(timer);
    }, [currentStep]);

    return (
        <nav className="mx-auto mb-6 mt-8 w-full px-3 print:hidden" aria-label="Progress">
            <div className="grid grid-cols-6">
                {steps.map((step: Step, idx: number) => {
                    const isCurrent = idx === currentStep;
                    const isPast = idx < currentStep;
                    const canClick = idx <= currentStep;
                    const isFirst = idx === 0;

                    return (
                        <div key={step.label} className="relative flex flex-col items-center">
                            {/* Connector line from previous step */}
                            {!isFirst && (
                                <div className="absolute right-1/2 top-[0.6rem] h-[2px] w-full">
                                    <div className="absolute inset-0 bg-secondary-500" />
                                    <div
                                        className={`absolute inset-0 origin-left bg-primary transition-transform duration-500 ease-out ${
                                            isPast || isCurrent ? 'scale-x-100' : 'scale-x-0'
                                        }`}
                                    />
                                </div>
                            )}

                            {/* Step button */}
                            <button
                                type="button"
                                onClick={() => canClick && setCurrentStep(idx)}
                                disabled={!canClick}
                                className={`group relative flex flex-col items-center transition-all duration-300 ${
                                    canClick ? 'cursor-pointer' : 'cursor-not-allowed'
                                }`}
                                title={step.label}
                                aria-current={isCurrent ? 'step' : undefined}
                            >
                                {/* Pulsing ring for current step - delayed until scroll finishes */}
                                {isCurrent && showAnimation && (
                                    <span className="absolute left-1/2 top-0 h-[1.25rem] w-[1.25rem] -translate-x-1/2 rounded-full bg-primary/40 step-dot-pulse" />
                                )}

                                {/* Step dot */}
                                <span
                                    className={`relative z-10 flex h-[1.25rem] w-[1.25rem] items-center justify-center rounded-full border-2 border-white transition-all duration-300 ${
                                        isCurrent
                                            ? `bg-primary ${showAnimation ? 'step-dot-active' : ''}`
                                            : isPast
                                            ? 'bg-secondary-300'
                                            : 'bg-secondary-500 group-hover:bg-secondary-400'
                                    }`}
                                >
                                    {isPast && (
                                        <svg
                                            className="h-2 w-2 text-white"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M4 10l4 4 8-8" className="step-checkmark" />
                                        </svg>
                                    )}
                                </span>

                                {/* Step label */}
                                <span
                                    className={`mt-2 whitespace-nowrap text-sm transition-all duration-300 ${
                                        isCurrent
                                            ? 'font-bold text-secondary-200'
                                            : isPast
                                            ? 'hidden font-medium text-secondary-300 md:inline'
                                            : 'hidden font-normal text-secondary-500 md:inline'
                                    } ${canClick && !isCurrent ? 'group-hover:text-secondary-300' : ''}`}
                                >
                                    {step.label}
                                </span>
                            </button>
                        </div>
                    );
                })}
            </div>
        </nav>
    );
}

export default ServiceBookingApp;
